<script setup lang="ts">
import { computed } from 'vue';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ClockIcon } from '@heroicons/vue/24/solid';
import { SOURCE_TYPES } from '@/stores/trackMatchingStore';
import type { TrackMatchingActionRow } from '@/trackMatching/useTrackMatchingQueueActions';
import { useHistoryInspector } from '@/composables/useHistoryInspector';

/**
 * Per-row "View track history" control for the Track Matching page (SAU-138).
 *
 * Lives in its own table column (no longer inside the Actions column). Renders
 * only for live-track rows that actually have a history entry to view —
 * tracks with no recorded actions (latest_live_track_action_id == null) get an
 * empty cell so the page doesn't dangle an action that opens an empty inspector
 * (SAU-172). Raw-DDEX / Spotify / expand-control rows also render nothing.
 */
const props = defineProps<{
  row: TrackMatchingActionRow;
}>();

const { openTrackHistory } = useHistoryInspector();

/**
 * The Elasticsearch source for a live-track row carries `latest_live_track_action_id`;
 * null/undefined means the track has never had an action queued or completed.
 */
const hasHistory = computed<boolean>(() => {
  const rawId = props.row.latest_live_track_action_id;
  if (rawId === null || rawId === undefined || rawId === '') {
    return false;
  }
  const numericId = Number(rawId);

  return Number.isFinite(numericId) && numericId > 0;
});

/** Open the History Inspector seeded to this live track's action history. */
function openTrackHistoryFor(): void {
  const liveTrackId = Number(props.row.live_track_id);
  if (!Number.isFinite(liveTrackId)) {
    return;
  }
  const code =
    typeof props.row.code === 'string' && props.row.code ? props.row.code : null;
  const name =
    typeof props.row.name === 'string' && props.row.name ? props.row.name : null;
  const title = name ?? code ?? `Track ${liveTrackId}`;
  openTrackHistory(liveTrackId, `${title} — history`);
}
</script>

<template>
  <Tooltip v-if="row.source === SOURCE_TYPES.LIVE_TRACK && hasHistory">
    <TooltipTrigger asChild>
      <Button
        size="icon"
        variant="ghost"
        aria-label="View track history"
        class="group hover:text-accent-foreground"
        data-testid="track-matching-history-button"
        @click="openTrackHistoryFor"
      >
        <ClockIcon class="w-4 h-4 group-hover:text-accent-foreground" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>
      <p>View track history</p>
    </TooltipContent>
  </Tooltip>
</template>
